import { useCallback, useEffect, useState } from "react";
import { Button, Card, Cluster, EmptyState, Input, Stack, useToast } from "@salesapp/ui";
import { opsApi, type ConfigurableFieldOptions } from "../../lib/opsApi";
import { reportSaveBlocked, reportSaveFailure } from "../../lib/saveNotice";
import { ListWorkspace } from "./RecordWorkspace";
import { optionsCsv } from "./SearchBar";

// Order controls display order below; entities not listed fall back to their raw key, sorted last.
const ENTITY_LABELS: Record<string, string> = {
  company: "Companies",
  lead: "Leads",
  product: "Products",
  opportunity: "Opportunities",
  quote: "Quotes",
  order: "Orders",
  activity: "Activities",
};
const ENTITY_ORDER = Object.keys(ENTITY_LABELS);

export function FieldOptionsPage({ orgId }: { orgId: string }) {
  const { show } = useToast();
  const [fields, setFields] = useState<ConfigurableFieldOptions[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const result = await opsApi.listFieldOptions(orgId);
      setFields(result.fields);
      setDrafts(
        Object.fromEntries(
          result.fields.map((field) => [rowKey(field), optionsCsv(field.options)]),
        ),
      );
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Field options could not be loaded.");
    }
  }, [orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  function rowKey(field: { entity: string; field_key: string }): string {
    return `${field.entity}:${field.field_key}`;
  }

  function groupByEntity(items: ConfigurableFieldOptions[]): Record<string, ConfigurableFieldOptions[]> {
    const groups: Record<string, ConfigurableFieldOptions[]> = {};
    for (const field of items) {
      (groups[field.entity] ??= []).push(field);
    }
    const ordered: Record<string, ConfigurableFieldOptions[]> = {};
    for (const entity of ENTITY_ORDER) {
      if (groups[entity]) ordered[entity] = groups[entity];
    }
    for (const entity of Object.keys(groups)) {
      if (!(entity in ordered)) ordered[entity] = groups[entity];
    }
    return ordered;
  }

  async function save(field: ConfigurableFieldOptions, override?: string[]) {
    const key = rowKey(field);
    const options = override ?? (drafts[key] ?? "").split(",").map((item) => item.trim()).filter(Boolean);
    if (options.length === 0) {
      setError(reportSaveBlocked(show, `${field.label} needs at least one option.`));
      return;
    }
    setSavingKey(key);
    setError(null);
    try {
      const updated = await opsApi.saveFieldOptions(orgId, field.entity, field.field_key, options);
      setFields((current) =>
        (current ?? []).map((item) => (rowKey(item) === key ? updated : item)),
      );
      setDrafts((current) => ({ ...current, [key]: optionsCsv(updated.options) }));
      show(override ? `${field.label} options restored to defaults.` : `${field.label} options saved.`, "success");
    } catch (err) {
      setError(reportSaveFailure(show, err, "Options could not be saved."));
    } finally {
      setSavingKey(null);
    }
  }

  return (
    <ListWorkspace title="Field options">
      {error && <p className="crm-error">{error}</p>}
      {!fields ? (
        <EmptyState title="Loading field options" description="Dropdown lists load together." />
      ) : fields.length === 0 ? (
        <EmptyState
          title="No configurable dropdowns"
          description="There are no shared option lists on this plan yet."
        />
      ) : (
        <Stack gap={6}>
          {Object.entries(groupByEntity(fields)).map(([entity, entityFields]) => (
            <Stack key={entity} gap={3}>
              <h2 className="crm-field-group-title">{ENTITY_LABELS[entity] ?? entity}</h2>
              <Stack gap={4}>
                {entityFields.map((field) => {
                  const key = rowKey(field);
                  const dirty = drafts[key] !== optionsCsv(field.options);
                  const defaults = field.default_options ?? [];
                  const atDefaults = optionsCsv(field.options) === optionsCsv(defaults);
                  return (
                    <Card key={key}>
                      <Stack gap={2}>
                        <h3 className="crm-section-title">{field.label}</h3>
                        <p className="crm-muted">
                          {ENTITY_LABELS[entity] ?? entity} · {field.label} — used across boards, filters, and
                          record forms wherever this field appears on that tab.
                        </p>
                        <Input
                          label="Options (comma-separated)"
                          value={drafts[key] ?? ""}
                          onChange={(event) =>
                            setDrafts((current) => ({ ...current, [key]: event.target.value }))
                          }
                        />
                        {defaults.length > 0 && (
                          <p className="crm-muted">
                            Defaults: <strong>{optionsCsv(defaults)}</strong>
                            {atDefaults && " (currently in use)"}
                          </p>
                        )}
                        <Cluster gap={2}>
                          <Button
                            onClick={() => save(field)}
                            disabled={!dirty || savingKey === key}
                          >
                            {savingKey === key ? "Saving…" : "Save"}
                          </Button>
                          {defaults.length > 0 && (
                            <Button
                              variant="secondary"
                              onClick={() => save(field, defaults)}
                              disabled={atDefaults || savingKey === key}
                            >
                              Restore defaults
                            </Button>
                          )}
                        </Cluster>
                      </Stack>
                    </Card>
                  );
                })}
              </Stack>
            </Stack>
          ))}
        </Stack>
      )}
    </ListWorkspace>
  );
}
